import { token } from '@atlaskit/tokens';
import { AsyncSelect, type StylesConfig } from '@databases/atlaskit/Select';
import DatasourcesRenderComponent, {
  type FieldTypeWithOnlyValues,
} from '@databases/field-value';
import { messages } from '@databases/intl/messages';
import { logError } from '@databases/logs';
import {
  type JiraIssueRef,
  type ResolvedJiraIssueRef,
} from '@databases/types/confluence';
import { type DatabaseFieldType } from '@databases/types/database';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { PopoverEdit } from '../PopoverEdit';
import {
  baseSelectStyleConfig,
  customPlaceholderAndControlSpacingStyling,
} from '../popover-edit-common';
import { usePopoverSoftSelection } from '../popover-edit-common/usePopoverSoftSelection';
import { type EditComponentProps } from '../types';
import { withJiraWorkItemCheck } from '../withJiraWorkItemCheck';

import { useJiraPriorityData } from './JiraPriorityUtil';
import { getPriorities, type PriorityItem, type PriorityItemValue } from './api';

interface PriorityOption {
  label: string;
  value: string;
  priority: PriorityItem;
}

const JiraPriorityPopover: FunctionComponent<
  EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>
> = ({
  contentId,
  entryId,
  field,
  onFocus,
  onBlur,
  isRenderedInSidePanel,
  autoFocus,
  isBulkEdit,
}) => {
  usePopoverSoftSelection({
    entryId,
    fieldId: field.id,
    isRenderedInSidePanel,
  });

  const data = useJiraPriorityData(contentId, entryId, field);

  const addDataSection = useMemo(
    () => (
      <ul style={{ listStyleType: 'none', padding: '0' }}>
        {data.issueRefs?.map((issueRef: JiraIssueRef, i: number) => (
          <li key={`${issueRef.key || 'issue'}-${i}`}>
            <PriorityEditField
              issueRef={issueRef}
              priorityItem={data.priorityItems[i]}
              resolvedIssue={data.resolvedIssues[i]}
              updatePriority={data.updatePriority}
              isUpdating={data.isUpdating}
              issueRefs={data.issueRefs}
              onFocus={onFocus}
              onBlur={onBlur}
              autoFocus={autoFocus}
            />
          </li>
        ))}
      </ul>
    ),
    [data, onFocus, onBlur, autoFocus]
  );

  if (!data.issueRefs?.length) {
    return null;
  }

  return (
    <PopoverEdit
      isMultiSelect={false}
      hasCurrentData={false}
      addDataSection={addDataSection}
      isRenderedInSidePanel={isRenderedInSidePanel}
      fieldWidth={field.width}
      isBulkEdit={isBulkEdit}
    />
  );
};

const JiraPriorityValueEdit = withJiraWorkItemCheck('priority', JiraPriorityPopover);

const PriorityEditField: FunctionComponent<{
  issueRef: JiraIssueRef;
  priorityItem?: FieldTypeWithOnlyValues;
  resolvedIssue: ResolvedJiraIssueRef | undefined;
  updatePriority: (
    issueRef: JiraIssueRef,
    priorityId: string,
    currentPriorityId?: string
  ) => Promise<void>;
  isUpdating: (issueRef: JiraIssueRef) => boolean;
  issueRefs: JiraIssueRef[];
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
}> = ({
  issueRef,
  priorityItem,
  resolvedIssue,
  updatePriority,
  isUpdating,
  issueRefs,
  onFocus,
  onBlur,
  autoFocus,
}) => {
  const intl = useIntl();

  const shouldPreload = issueRefs?.length === 1;
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const shouldAutoOpen = shouldPreload && !hasAutoOpened;
  const [pendingSelection, setPendingSelection] = useState<PriorityOption | null>(null);

  useEffect(() => {
    setHasAutoOpened(false);
  }, [issueRef?.key, issueRef?.ari, shouldPreload]);

  const loadOptions = useCallback(async (): Promise<PriorityOption[]> => {
    if (!issueRef || !resolvedIssue?.ari || !resolvedIssue?.projectId) {
      return [];
    }

    try {
      const priorities = await getPriorities({
        issueAri: resolvedIssue.ari,
        projectId: resolvedIssue.projectId,
        intl,
      });

      return priorities.map((p) => ({
        label: p.name,
        value: p.id,
        priority: p,
      }));
    } catch (error) {
      logError(
        'jira-priority/load-options:failed',
        error instanceof Error ? error : new Error('Failed to load priority options')
      );
      return [];
    }
  }, [issueRef, resolvedIssue, intl]);

  // Preload options for single issues by calling loadOptions in background
  useEffect(() => {
    if (shouldPreload && issueRef && resolvedIssue?.ari && resolvedIssue?.projectId) {
      loadOptions();
    }
  }, [shouldPreload, issueRef, resolvedIssue, loadOptions]);

  const formatOptionLabel = useCallback((option: PriorityOption) => {
    const item = {
      type: 'icon' as const,
      text: option.label,
      values: [
        {
          label: option.priority.name,
          source: option.priority.iconUrl || '',
        },
      ],
    } as FieldTypeWithOnlyValues;

    return <DatasourcesRenderComponent item={item} />;
  }, []);

  const currentValue = useMemo((): PriorityOption | undefined => {
    if (isUpdating(issueRef) && pendingSelection !== null) {
      return pendingSelection;
    }

    if (!priorityItem?.values?.[0]) {
      return undefined;
    }

    const current = priorityItem.values[0] as PriorityItemValue;

    return {
      label: current.text,
      value: current.id,
      priority: {
        id: current.id,
        name: current.text,
        iconUrl: current.source,
      },
    };
  }, [isUpdating, issueRef, pendingSelection, priorityItem]);

  const handleChange = useCallback(
    async (option: PriorityOption | null) => {
      if (option && resolvedIssue?.ari) {
        setPendingSelection(option);

        try {
          await updatePriority(
            { ...issueRef, ari: resolvedIssue.ari, projectId: resolvedIssue.projectId },
            option.value,
            currentValue?.value // Pass current priority ID for same-priority prevention
          );
        } finally {
          setPendingSelection(null);
        }
      }
    },
    [issueRef, resolvedIssue, updatePriority, currentValue?.value]
  );

  const styles: StylesConfig<PriorityOption, boolean> = {
    singleValue: (base) => ({
      ...base,
      div: {
        alignItems: 'center',
      },
      padding: 0,
      margin: 0,
    }),
    valueContainer: (base) => ({
      ...base,
      // override valueContainer to match the select options' padding
      paddingLeft: token('space.150', '12px'),
      paddingRight: token('space.150', '12px'),
      paddingBottom: token('space.075', '6px'),
      paddingTop: token('space.075', '6px'),
    }),
    control: (base) => ({
      ...base,
      padding: 0,
      margin: 0,
      border: 0,
    }),
    option: (base) => ({
      ...base,
      div: {
        alignItems: 'center',
      },
    }),
  };

  return (
    <AsyncSelect<PriorityOption>
      value={currentValue}
      autoFocus={autoFocus}
      placeholder={intl.formatMessage(messages['jira-priority.placeholder'])}
      loadOptions={loadOptions}
      formatOptionLabel={formatOptionLabel}
      getOptionValue={(option) => option.value}
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      onFocus={onFocus}
      onBlur={onBlur}
      noOptionsMessage={() => intl.formatMessage(messages['jira-priority.no-options'])}
      isLoading={isUpdating(issueRef)}
      isSearchable={false}
      menuPortalTarget={document.body}
      defaultMenuIsOpen={shouldAutoOpen}
      onMenuOpen={() => {
        if (shouldPreload) {
          setHasAutoOpened(true);
        }
      }}
      styles={
        {
          ...baseSelectStyleConfig,
          ...customPlaceholderAndControlSpacingStyling,
          ...styles,
        } as StylesConfig<PriorityOption, boolean>
      }
    />
  );
};

export default JiraPriorityValueEdit;
